import { useState } from 'react';
import { motion } from 'framer-motion';
import { ImageSlideData, Theme, slideTypographyStyle } from '../slideTypes';
import { SettingsButton, SlideSettingsDrawer, DrawerSection, DrawerInput, DrawerCheckbox } from '../shared/SlideShared';
import { ImageDropZone } from '../ImageDropZone';
import { FocalPointPicker, focalPos } from '../FocalPointPicker';
import { UnsplashCredit, withImageAttribution } from '../shared/UnsplashCredit';
import { springs } from '../../../ui/slide-animations';

export function ImageSlide({
  data,
  theme,
  isEditing,
  onUpdate,
}: {
  data: ImageSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ImageSlideData) => void;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const settingsDrawer = isEditing && (
    <SlideSettingsDrawer
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title="Image Settings"
    >
      <DrawerSection label="Image">
        <ImageDropZone
          compact
          onImageUrl={(url, attribution) => onUpdate(withImageAttribution({ ...data, url }, attribution))}
          currentUrl={data.url || undefined}
          primaryColor="var(--accent)"
        />
      </DrawerSection>
      {data.url && (
        <DrawerSection label="Focal point">
          <FocalPointPicker
            imageUrl={data.url}
            value={data.focalPoint}
            onChange={(fp) => onUpdate({ ...data, focalPoint: fp })}
          />
        </DrawerSection>
      )}
      <DrawerSection label="Caption (optional)">
        <DrawerInput
          type="text"
          value={data.caption || ''}
          onChange={(e) => onUpdate({ ...data, caption: e.target.value })}
          placeholder="Caption..."
        />
      </DrawerSection>
      <DrawerCheckbox
        id="overlay-toggle"
        checked={!!data.overlay}
        onChange={(v) => onUpdate({ ...data, overlay: v })}
        label="Dark overlay"
      />
    </SlideSettingsDrawer>
  );

  if (!data.url) {
    return (
      <div className="relative h-full group" style={{ backgroundColor: theme.colors.surface }}>
        {isEditing ? (
          <ImageDropZone
            onImageUrl={(url, attribution) => onUpdate(withImageAttribution({ ...data, url }, attribution))}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent, #3b82f6) 10%, transparent)' }}
            >
              <svg className="w-8 h-8" style={{ color: 'var(--accent, #3b82f6)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-medium text-sm mb-0.5" style={{ color: theme.colors.text }}>Image</p>
              <p className="text-xs" style={{ color: theme.colors.textMuted }}>No image configured</p>
            </div>
          </div>
        )}
        {settingsDrawer}
      </div>
    );
  }

  return (
    <div className="relative h-full group">
      <motion.div
        className="w-full h-full"
        initial={!isEditing ? { opacity: 0, scale: 1.05 } : false}
        animate={{ opacity: 1, scale: 1 }}
        transition={springs.smooth as object}
      >
        <img
          src={data.url}
          alt={data.caption || ''}
          className="w-full h-full object-cover"
          style={{ objectPosition: focalPos(data.focalPoint) }}
        />
      </motion.div>
      {data.overlay && (
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      )}
      {data.caption && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 px-8 py-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none"
          initial={!isEditing ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...(springs.gentle as object), delay: 0.3 }}
        >
          <p
            className="text-white"
            style={{
              ...slideTypographyStyle(theme, 'caption', theme.fonts.body),
              color: 'white',
            }}
          >
            {data.caption}
          </p>
        </motion.div>
      )}
      <UnsplashCredit author={data.unsplashAuthor} authorUrl={data.unsplashAuthorUrl} />
      {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}

      {settingsDrawer}
    </div>
  );
}
